<template>
  <rest-page :fetch-endpoint="fetchEndpoint">
    <page-header :breadcrumbs="breadcrumbs" :title="title">
      <template slot="actions">
        <el-button
          type="primary"
          @click="createProfile"
          data-qa="create-profile-btn"
        >
          {{ $t('Profiles_Button_CreateProfile') }}
        </el-button>
      </template>
    </page-header>

    <page-content>
      <rest-form
        v-if="model"
        ref="form"
        :save-endpoint="saveEndpoint"
        :after-save="afterSave"
        :model="model"
        :rules="rules"
      >
        <form-section :title="$t('Profiles_Create_BasicData')">
          <el-row>
            <el-col :span="12">
              <rest-form-item
                :label="$t('Profiles_Field_UserId')"
                prop="user_id"
              >
                <el-input
                  v-model="model.user_id"
                  :disabled="model.id && model.user_id"
                  data-qa="user_id"
                />
              </rest-form-item>
            </el-col>
            <el-col
              :span="12"
              v-if="$store.state.app.features.customerIdEnabled"
            >
              <rest-form-item
                :label="$t('Profiles_Field_CustomerId')"
                prop="customer_id"
              >
                <el-input v-model="model.customer_id" data-qa="customer_id" />
              </rest-form-item>
            </el-col>

            <el-col :span="12">
              <rest-form-item
                :label="$t('UserProfiles_Field_MobileNumber')"
                prop="mobile_number"
              >
                <el-input
                  v-model="model.mobile_number"
                  data-qa="mobile_number"
                />
              </rest-form-item>
            </el-col>

            <el-col :span="12">
              <rest-form-item :label="$t('Profiles_Field_Email')" prop="email">
                <el-input v-model="model.email" data-qa="email" />
              </rest-form-item>
            </el-col>

            <el-col :span="12">
              <rest-form-item
                :label="$t('Profiles_Field_WhatsappNumber')"
                prop="whatsapp_number"
              >
                <el-input
                  v-model="model.whatsapp_number"
                  data-qa="whatsapp_number"
                />
              </rest-form-item>
            </el-col>
          </el-row>
        </form-section>

        <form-section :title="$t('Profiles_Create_ContactSettings')">
          <el-row>
            <el-col :span="12">
              <rest-form-item
                :label="$t('Profiles_Field_Language')"
                prop="overrideLanguage"
              >
                <el-select
                  v-model="model.overrideLanguage"
                  :filterable="true"
                  data-qa="override_language"
                >
                  <el-option
                    v-for="(option, index) in languages"
                    :key="index"
                    :value="index"
                    :label="option"
                  ></el-option>
                </el-select>
              </rest-form-item>
            </el-col>

            <el-col :span="12">
              <rest-form-item
                :label="$t('Profiles_Field_Timezone')"
                prop="overrideTimezone"
              >
                <el-select
                  v-model="model.overrideTimezone"
                  :filterable="true"
                  data-qa="override_timezone"
                >
                  <el-option
                    v-for="(option, index) in timezones"
                    :key="index"
                    :value="index"
                    :label="option"
                  ></el-option>
                </el-select>
              </rest-form-item>
            </el-col>
          </el-row>

          <el-row>
            <el-col :span="12">
              <rest-form-item
                :label="$t('Profiles_Field_EmailSubscription')"
                prop="email_subscription"
              >
                <enum-select
                  v-model="model.email_subscription"
                  :options="subscriptionStatus"
                  :number="true"
                  data-qa="email_subscription"
                />
              </rest-form-item>
            </el-col>

            <el-col :span="12">
              <rest-form-item
                :label="$t('Profiles_Field_SmsSubscription')"
                prop="sms_subscription"
              >
                <enum-select
                  v-model="model.sms_subscription"
                  :options="subscriptionStatus"
                  :number="true"
                  data-qa="sms_subscription"
                />
              </rest-form-item>
            </el-col>

            <el-col :span="12">
              <rest-form-item
                :label="$t('Profiles_Field_WhatsappSubscription')"
                prop="whatsapp_subscription"
              >
                <enum-select
                  v-model="model.whatsapp_subscription"
                  :options="subscriptionStatus"
                  :number="true"
                  data-qa="whatsapp_subscription"
                />
              </rest-form-item>
            </el-col>
          </el-row>
        </form-section>
      </rest-form>
    </page-content>
  </rest-page>
</template>

<script>
import RestPage from '@/common/components/rest/RestPage'
import PageHeader from '@/common/components/page/PageHeaderNew'
import PageContent from '@/common/components/page/PageContent'
import FormSection from '@/common/components/form/FormSection'
import RestForm from '@/common/components/rest/RestForm'
import RestFormItem from '@/common/components/rest/RestFormItem'
import EnumSelect from '@/common/components/form/EnumSelect'
import { subscriptionStatus } from '@/modules/profiles/enum/SubscriptionStatusEnum'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import parsePhoneNumber from 'libphonenumber-js'
import { $hasFeature } from '@/core/utils/VueAcl'

export default {
  name: 'ProfileCreatePage',

  components: {
    RestPage,
    PageHeader,
    PageContent,
    FormSection,
    RestForm,
    RestFormItem,
    EnumSelect
  },

  data() {
    return {
      model: this.defaultModel(),
      timezones: [],
      languages: [],
      rules: {
        mobile_number: {
          validator: (rule, value, callback) => {
            if (!value) {
              callback()
            } else {
              if (!value.startsWith('+')) {
                value = '+' + value
              }
              const phoneNumber = parsePhoneNumber(value)
              if (
                $hasFeature('disableMobileValidation') ||
                (!isNaN(value) && phoneNumber && phoneNumber.isValid())
              ) {
                callback()
              } else {
                callback(new Error())
              }
            }
          },
          message: this.$t('Profiles_MobileNumber_IncorrectPhoneNumber')
        },
        whatsapp_number: {
          validator: (rule, value, callback) => {
            if (!value) {
              callback()
            } else {
              if (!value.startsWith('+')) {
                value = '+' + value
              }
              const phoneNumber = parsePhoneNumber(value)
              if (
                $hasFeature('disableMobileValidation') ||
                (!isNaN(value) && phoneNumber && phoneNumber.isValid())
              ) {
                callback()
              } else {
                callback(new Error())
              }
            }
          },
          message: this.$t('Profiles_MobileNumber_IncorrectPhoneNumber')
        }
      },
      subscriptionStatus
    }
  },

  computed: {
    title() {
      return this.$t(this.$route.meta.title)
    },

    breadcrumbs() {
      return [
        {
          label: this.$t('Users_Breadcrumb_Data')
        },
        {
          label: this.$t('Users_Breadcrumb_Profiles'),
          route: projectRoute({
            name: 'Profiles'
          })
        },
        {
          label: this.$t('Users_Breadcrumb_CreateProfile')
        }
      ]
    },

    fetchEndpoint() {
      return [this.fetchTimezonesEndpoint, this.fetchLanguagesEndpoint]
    },

    fetchTimezonesEndpoint() {
      return {
        method: 'get',
        url: `timezones`,
        handler: data => {
          this.timezones = data
        }
      }
    },

    fetchLanguagesEndpoint() {
      return {
        method: 'get',
        url: `languages`,
        handler: data => {
          this.languages = this.sortLanguages(data)
        }
      }
    },

    saveEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `user-profiles`
      })
    },

    features() {
      return this.$store.state.app.features
    }
  },

  methods: {
    defaultModel() {
      return {
        email_subscription: 2,
        sms_subscription: 2,
        whatsapp_subscription: 2
      }
    },

    afterSave(model) {
      this.$router.push(
        projectRoute({
          name: 'ProfileData',
          query: {
            profile_id: model.id
          }
        })
      )
    },

    createProfile() {
      this.$refs['form'].save()
    },

    sortLanguages(languages) {
      const orderedArray = Object.values(languages).sort()
      let ordered = {}

      orderedArray.forEach(element => {
        let key = Object.keys(languages).find(
          item => languages[item] === element
        )
        ordered[key] = element
      })
      return ordered
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/profiles/pages/ProfileCreatePage.vue